import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePipelineDetail, useArchivePipeline, useRestorePipeline } from "@/hooks/usePipeline";
import { CANONICAL_STATES, resolveJobScore, scoreTone } from "@/format";
import { cn } from "@/cn";
import { PipelineStagePanel } from "@/components/pipeline/pipeline-stage-panel";
import {
  canArchivePipeline,
  defaultStageTab,
  isPipelineArchived,
  isValidStageName,
  type PipelineStageName,
} from "@/lib/pipeline-stages";

const TONE_BG: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-400",
  warn: "bg-amber-500/10 text-amber-400",
  bad: "bg-red-500/10 text-red-400",
  muted: "bg-zinc-500/10 text-muted-foreground",
};

export function PipelineWorkflowPage() {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePipelineDetail(pipelineId);
  const archiveMutation = useArchivePipeline();
  const restoreMutation = useRestorePipeline();

  const stageParam = searchParams.get("stage") ?? "";
  const activeStage: PipelineStageName = isValidStageName(stageParam)
    ? stageParam
    : data
      ? defaultStageTab(data.pipeline.currentStage)
      : "Evaluated";

  const setActiveStage = (stage: PipelineStageName) => {
    setSearchParams({ stage }, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error || !data?.pipeline || !data.job) {
    return (
      <div className="mx-auto max-w-3xl">
        <Link to="/pipeline" className="mb-6 inline-flex text-sm text-brand hover:underline">
          ← Back to Pipeline
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Pipeline not found.
        </div>
      </div>
    );
  }

  const { pipeline, stages, actions, job, company } = data;
  const sortedStages = [...stages].sort((a, b) => a.order - b.order);
  const displayScore = resolveJobScore(job);
  const archived = isPipelineArchived(pipeline);
  const showArchive = canArchivePipeline(pipeline);

  const handleArchive = () => {
    if (!window.confirm("Archive this pipeline? It will move to the Archived tab.")) return;
    archiveMutation.mutate(pipeline.pipelineId ?? pipeline.id, {
      onSuccess: () => navigate("/pipeline"),
    });
  };

  const handleRestore = () => {
    restoreMutation.mutate(pipeline, {
      onSuccess: () => navigate("/pipeline"),
    });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/pipeline" className="mb-4 inline-flex items-center gap-1 text-sm text-brand hover:underline">
        ← Pipeline
      </Link>

      <header className="mb-6 rounded-lg border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-foreground">{job.company}</h1>
            <p className="text-lg text-muted-foreground">{job.role}</p>
            {company && (
              <Link
                to={`/companies/${company.slug}`}
                className="mt-1 inline-block text-xs text-brand hover:underline"
              >
                {company.name}
              </Link>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {displayScore !== undefined && (
              <span
                className={cn(
                  "rounded-full px-3 py-1 font-mono text-sm font-semibold",
                  TONE_BG[scoreTone(String(displayScore))],
                )}
              >
                {displayScore}/5
              </span>
            )}
            <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-medium text-brand">
              {pipeline.currentStage ?? "Evaluated"}
            </span>
          </div>
        </div>
        {job.url && (
          <a
            href={job.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block truncate rounded-md bg-muted/30 px-3 py-2 text-xs text-brand hover:underline"
          >
            {job.url}
          </a>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
          <Link to={`/jobs/${job.id}`} className="text-brand hover:underline">
            Job details (enrich & research)
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link
            to={`/ai-assistant?pipelineId=${encodeURIComponent(pipeline.pipelineId ?? pipeline.id)}&stage=${encodeURIComponent(activeStage)}${job.id ? `&jobId=${encodeURIComponent(job.id)}` : ""}`}
            className="text-brand hover:underline"
          >
            Ask AI
          </Link>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground capitalize">{pipeline.status}</span>
          {archived && pipeline.archivedAt && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">
                Archived {new Date(pipeline.archivedAt).toLocaleDateString()}
              </span>
            </>
          )}
          {showArchive && (
            <button
              type="button"
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
              className="rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
            >
              {archiveMutation.isPending ? "Archiving…" : "Archive pipeline"}
            </button>
          )}
          {archived && (
            <button
              type="button"
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
              className="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground hover:bg-muted/20 disabled:opacity-50"
            >
              {restoreMutation.isPending ? "Restoring…" : "Restore to active"}
            </button>
          )}
        </div>
      </header>

      {archived && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          This pipeline is archived. Restore it to return it to your active list.
        </div>
      )}

      <nav
        className="mb-6 flex gap-1 overflow-x-auto border-b border-border pb-px"
        aria-label="Pipeline stages"
      >
        {CANONICAL_STATES.map((name) => {
          const meta = sortedStages.find((s) => s.name === name);
          const isActive = activeStage === name;
          const isCurrent = pipeline.currentStage === name;
          const isDone = meta?.status === "completed";

          return (
            <button
              key={name}
              type="button"
              onClick={() => setActiveStage(name)}
              className={cn(
                "relative shrink-0 px-3 py-2.5 text-xs font-medium transition",
                isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span className="flex items-center gap-1.5">
                {isDone && <span className="text-emerald-500">✓</span>}
                {name}
                {isCurrent && !isActive && (
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden />
                )}
              </span>
              {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand" />}
            </button>
          );
        })}
      </nav>

      <PipelineStagePanel
        pipelineId={pipeline.id}
        stageName={activeStage}
        job={job}
        stages={sortedStages}
        actions={actions}
        currentStage={pipeline.currentStage}
        onStageAdvanced={(target) => {
          setActiveStage(target);
          navigate(`/pipeline/${pipeline.id}?stage=${encodeURIComponent(target)}`, { replace: true });
        }}
      />
    </div>
  );
}
